<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Delete03Icon } from '@hugeicons/core-free-icons';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import DeleteChannelDialog from 'dashboard/components-next/Settings/DeleteChannelDialog.vue';
import SettingsDangerButton from 'dashboard/components-next/Settings/SettingsDangerButton.vue';
import SettingsWarningBanner from 'dashboard/components-next/Settings/SettingsWarningBanner.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();

const deleteDialogRef = ref(null);
const isDeleting = ref(false);

const openDeleteDialog = () => {
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('inboxes/delete', props.inbox.id);
    deleteDialogRef.value?.close();
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    router.replace({
      name: 'settings_inbox_list',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <section v-if="isAdmin" class="w-full border-t border-n-weak pt-4">
    <h2 class="mb-4 text-heading-2 text-n-slate-12">
      {{ $t('INBOX_MGMT.DANGER_ZONE.TITLE') }}
    </h2>

    <SettingsWarningBanner
      class="mb-4"
      :message="$t('INBOX_MGMT.DANGER_ZONE.WARNING')"
    />

    <SettingsDangerButton
      :label="$t('INBOX_MGMT.DANGER_ZONE.DELETE_CHANNEL_BUTTON')"
      @click="openDeleteDialog"
    >
      <template #icon>
        <HugeIcon :icon="Delete03Icon" :size="16" class="shrink-0" />
      </template>
    </SettingsDangerButton>

    <DeleteChannelDialog
      ref="deleteDialogRef"
      :channel-name="inbox.name"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </section>
</template>
